import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 6; // ~12s

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | paid | timeout | error
  const [orderNumber, setOrderNumber] = useState(null);
  const { clear } = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setOrderNumber(data.order_number || null);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          if (!clearedRef.current) {
            clearedRef.current = true;
            clear();
          }
          return;
        }
        if (attempts >= POLL_MAX_ATTEMPTS) {
          setStatus('timeout');
          return;
        }
        attempts += 1;
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  if (status === 'checking') {
    return (
      <div data-testid="payment-success-checking" className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Loader2 className="w-10 h-10 mx-auto animate-spin text-lum-text2 mb-6" />
        <p className="overline mb-3">Confirming your payment</p>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">Almost there…</h1>
        <p className="mt-4 text-lum-text2">Hold tight — we&apos;re confirming with Stripe.</p>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div data-testid="payment-success-page" className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-lum-text text-lum-bg flex items-center justify-center mb-6">
          <Check />
        </div>
        <p className="overline mb-3">Payment confirmed</p>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05]">Your ritual is on the way.</h1>
        {orderNumber && <p className="mt-6 text-lum-text2">Order number: <span className="text-lum-text">{orderNumber}</span></p>}
        <p className="mt-4 text-lum-text2 max-w-lg mx-auto">A receipt is in your inbox. We&apos;ll email shipping confirmation within 24 hours. Your free gift will be packed alongside your masks.</p>
        <div className="mt-10 flex justify-center gap-4">
          <Link to="/shop" className="btn-primary inline-flex">Continue shopping</Link>
          <Link to="/" className="btn-secondary inline-flex">Back home</Link>
        </div>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div data-testid="payment-success-timeout" className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="overline mb-3">Still processing</p>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">Hang tight.</h1>
        <p className="mt-4 text-lum-text2">Your payment is taking a moment to confirm. Check your inbox shortly — if anything looks off, contact support.</p>
        <Link to="/" className="btn-primary mt-8 inline-flex">Back home</Link>
      </div>
    );
  }

  return (
    <div data-testid="payment-success-error" className="max-w-3xl mx-auto px-6 py-32 text-center">
      <p className="overline mb-3">Something went wrong</p>
      <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">We couldn&apos;t verify your payment.</h1>
      <p className="mt-4 text-lum-text2">Please refresh this page, or check your inbox for a Stripe receipt before retrying.</p>
      <Link to="/cart" className="btn-primary mt-8 inline-flex">Back to cart</Link>
    </div>
  );
}
